<template>
  <v-card
    class="overview-card-widget fill-height d-flex"
    :class="{ 'overview-card-widget--hoverable': interactable }"
    :link="false"
    @click="onWidgetClick()"
  >
    <div v-if="widgetDataValue.loading" class="px-6 w-100">
      <v-skeleton-loader type="list-item-avatar-two-line" class="bg-transparent" />
      <v-skeleton-loader type="list-item" class="bg-transparent" />
    </div>

    <v-card-text v-else-if="widgetDataValue.data" class="d-flex align-center flex-grow-1 pa-0">
      <div class="flex-grow-1 align-self-start">
        <div class="d-flex align-center">
          <v-icon size="48" :icon="iconMap[widgetConfig.type]" class="mr-2"></v-icon>
          <h3 class="overview-card-widget__value">{{ widgetDataValue.data.total }}</h3>
        </div>
        <div class="mt-1">
          <div class="subtitle2">
            {{ $t(`dashboard.overviewCardWidgets.${widgetConfig.type}.title`) }}
          </div>
          <div
            v-if="changeText"
            class="d-flex align-center mt-1 body3"
            data-testid="delta-text"
            :class="changeColorClass"
          >
            <span v-html="changeArrow"></span>
            <span>
              {{ changeText }}
            </span>
          </div>
        </div>
      </div>
      <v-icon v-if="!widgetConfig.metadata.restricted" icon="$chevronRight" color="grey"></v-icon>
    </v-card-text>

    <div
      v-else
      class="h-100 w-100 d-flex align-center justify-center flex-column px-6"
      data-testid="widget-error-state"
    >
      <v-icon icon="$alertTriangle" size="48"></v-icon>
      <span class="subtitle2 mb-4 text-center">
        {{ $t(`dashboard.overviewCardWidgets.${widgetConfig.type}.error`) }}</span
      >
      <div class="coro-link" @click.stop="$emit('fetch-data')">
        {{ $t("general.retry") }}
      </div>
    </div>
  </v-card>
</template>
<script lang="ts">
import { defineComponent, computed, type PropType } from "vue";
import type { OverviewCardWidgetConfiguration } from "@/_helpers/dashboard.ts";
import { OverviewCardWidgetType } from "@/constants/dashboard.ts";
import { useRouter } from "vue-router";
import { RouteName } from "@/constants/routes.ts";
import { TicketStatus } from "@/constants/tickets.ts";
import { useI18n } from "vue-i18n";
import { trackGtmEvent } from "@/_helpers/analytics";
import { GtmAction, GTMParams } from "@/constants/gtm.ts";
import startCase from "lodash/startCase";

export default defineComponent({
  props: {
    widgetConfig: {
      type: Object as PropType<OverviewCardWidgetConfiguration>,
      required: true,
    },
  },
  emits: ["fetch-data"],
  setup(props) {
    const i18n = useI18n();
    const router = useRouter();
    const iconMap: Record<OverviewCardWidgetType, string> = {
      [OverviewCardWidgetType.OPEN_TICKETS]: "$issueS",
      [OverviewCardWidgetType.AUTO_RESOLVED_TICKETS]: "$autoResolveS",
      [OverviewCardWidgetType.PROTECTED_DEVICES]: "$deviceS",
      [OverviewCardWidgetType.PROTECTED_USERS]: "$usersS",
    };

    const widgetDataValue = computed(() => props.widgetConfig.data);

    const interactable = computed(
      () => !props.widgetConfig.metadata.restricted && !widgetDataValue.value.error
    );

    // Computed property to determine the color of the change indicator
    const changeColorClass = computed(() => {
      const delta = widgetDataValue.value.data?.delta ?? 0;

      if (props.widgetConfig?.metadata.changeType === "static") {
        return "text-green-dark";
      }

      if (delta === 0) {
        return "text-indigo-medium";
      }

      const hasIncreased = delta > 0;
      const isIncreaseConsideredGood = props.widgetConfig?.metadata.changeType === "increase";

      // This is a "good" change if the direction of change matches the "good" direction.
      return hasIncreased === isIncreaseConsideredGood ? "text-green-dark" : "text-red-dark";
    });

    // Computed property to determine the change indicator icon
    const changeArrow = computed(() => {
      if (props.widgetConfig?.metadata.changeType === "static") {
        return "";
      }
      const delta = widgetDataValue.value.data?.delta ?? 0;
      if (delta > 0) {
        return "&#8593;"; // Up arrow
      }
      if (delta < 0) {
        return "&#8595;"; // Down arrow
      }
      return ""; // No arrow for zero change
    });

    // Computed property to format the change text
    const changeText = computed(() => {
      if (props.widgetConfig?.metadata.changeType === "static") {
        return i18n.t("dashboard.overviewCardWidgets.common.seeMoreInsights");
      }
      if (!widgetDataValue.value.data?.delta) {
        return i18n.t("coroImpact.noChangeFromLastMonth");
      }

      return `${Math.abs(widgetDataValue.value.data.delta)}% ${i18n.t("coroImpact.fromLastMonth")}`;
    });

    const onWidgetClick = async () => {
      if (!interactable.value) return;
      const overviewWidgetType = startCase(props.widgetConfig.type);
      trackGtmEvent(
        GtmAction.WORKSPACE_VIEW_DASHBOARD_WIDGET,
        `${GTMParams.OVERVIEW_WIDGET_CLICKED} - ${overviewWidgetType}`
      );
      switch (props.widgetConfig.type) {
        case OverviewCardWidgetType.AUTO_RESOLVED_TICKETS:
          await router.push({ name: RouteName.CORO_IMPACT_PAGE });
          break;
        case OverviewCardWidgetType.PROTECTED_USERS:
          await router.push({ name: RouteName.PROTECTED_USERS });
          break;
        case OverviewCardWidgetType.PROTECTED_DEVICES:
          await router.push({ name: RouteName.DEVICES });
          break;
        case OverviewCardWidgetType.OPEN_TICKETS:
          await router.push({ name: RouteName.TICKETS, query: { status: TicketStatus.OPEN } });
          break;
      }
    };

    return {
      widgetDataValue,
      changeArrow,
      interactable,
      changeColorClass,
      changeText,
      iconMap,
      onWidgetClick,
    };
  },
});
</script>

<style scoped lang="scss">
.overview-card-widget {
  padding: 12px 8px 12px 16px;
  &.overview-card-widget--hoverable {
    &:hover {
      cursor: pointer;
      box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.12) !important;
    }
  }

  &__value {
    font-family: Quicksand, sans-serif;
    font-size: 40px;
    font-style: normal;
    font-weight: 500;
    line-height: 105%; /* 42px */
    letter-spacing: -0.6px;
    word-break: break-all;
  }
}
</style>
